import React, { useState, useEffect } from 'react';
import { Building2, Search, Users, FileText, Calendar, AlertTriangle, CheckCircle, Plus, Trash2, RefreshCw, MapPin, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { API_URL } from '../../config/api';

const CompaniesHouse = () => {
  const [searchNumber, setSearchNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyData, setCompanyData] = useState(null);
  const [trackedCompanies, setTrackedCompanies] = useState([]);
  const [apiConfigured, setApiConfigured] = useState(null);

  useEffect(() => {
    checkApiStatus();
    loadTrackedCompanies();
  }, []);

  const checkApiStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies-house/status`);
      const data = await res.json();
      setApiConfigured(data.configured);
    } catch (err) {
      setApiConfigured(false);
    }
  };

  const loadTrackedCompanies = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies-house/tracked-companies`);
      const data = await res.json();
      setTrackedCompanies(data.companies || []);
    } catch (err) {
      console.error('Failed to load tracked companies:', err);
    }
  };

  const searchCompany = async (companyNumber) => {
    const number = companyNumber || searchNumber.trim().toUpperCase();
    if (!number) return;

    setLoading(true);
    setError('');
    setCompanyData(null);

    try {
      const res = await fetch(`${API_URL}/api/admin/companies-house/company/${number}/full-details`);
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.detail || 'Company not found');
      }
      const data = await res.json();
      setCompanyData(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const trackCompany = async () => {
    if (!companyData) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/admin/companies-house/tracked-companies?company_number=${companyData.company.number}&company_name=${encodeURIComponent(companyData.company.name)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        loadTrackedCompanies();
      }
    } catch (err) {
      console.error('Failed to track company:', err);
    }
  };

  const untrackCompany = async (companyNumber) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/companies-house/tracked-companies/${companyNumber}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        loadTrackedCompanies();
      }
    } catch (err) {
      console.error('Failed to untrack company:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'dissolved': return 'text-red-600 bg-red-50';
      case 'liquidation': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getDaysUntilDueColor = (days) => {
    if (days === null || days === undefined) return 'text-gray-500';
    if (days < 0) return 'text-red-600';
    if (days <= 14) return 'text-orange-600';
    if (days <= 30) return 'text-yellow-600';
    return 'text-green-600';
  };

  if (apiConfigured === false) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-red-700 mb-2">API Key Not Configured</h2>
          <p className="text-red-600">
            Please add your Companies House API key to the backend environment variables.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="companies-house-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <Building2 className="w-7 h-7 text-[#1a1f4e]" />
          Companies House
        </h1>
        <p className="text-gray-500 mt-1">Search and track company information for confirmation statements</p>
      </div>

      {/* Search Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Enter company number (e.g., 12345678)"
              value={searchNumber}
              onChange={(e) => setSearchNumber(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && searchCompany()}
              className="pl-10 h-12"
              data-testid="company-search-input"
            />
          </div>
          <Button
            onClick={() => searchCompany()}
            disabled={loading || !searchNumber.trim()}
            className="h-12 px-6 bg-[#1a1f4e] hover:bg-[#2a2f5e]"
            data-testid="company-search-btn"
          >
            {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Search'}
          </Button>
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Tracked Companies */}
      {trackedCompanies.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-[#1a1f4e]" />
            Tracked Companies
          </h2>
          <div className="space-y-2">
            {trackedCompanies.map((company) => (
              <div
                key={company.company_number}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <button
                  onClick={() => searchCompany(company.company_number)}
                  className="flex-1 text-left"
                >
                  <span className="font-medium text-gray-900">{company.company_name}</span>
                  <span className="text-gray-500 ml-2">({company.company_number})</span>
                </button>
                <button
                  onClick={() => untrackCompany(company.company_number)}
                  className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Company Details */}
      {companyData && (
        <div className="space-y-6" data-testid="company-details">
          {/* Company Overview */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{companyData.company.name}</h2>
                <p className="text-gray-500 mt-1">Company Number: {companyData.company.number}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium capitalize ${getStatusColor(companyData.company.status)}`}>
                  {companyData.company.status}
                </span>
                {!trackedCompanies.find(c => c.company_number === companyData.company.number) && (
                  <Button
                    onClick={trackCompany}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Track
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Company Type</h3>
                <p className="text-gray-900">{companyData.company.type || 'N/A'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Date of Incorporation</h3>
                <p className="text-gray-900">{formatDate(companyData.company.date_of_creation)}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Jurisdiction</h3>
                <p className="text-gray-900 capitalize">{companyData.company.jurisdiction?.replace(/-/g, ' ') || 'N/A'}</p>
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">SIC Codes</h3>
                <p className="text-gray-900">{companyData.company.sic_codes?.join(', ') || 'N/A'}</p>
              </div>
            </div>

            {companyData.company.registered_office_address && (
              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Registered Office Address
                </h3>
                <p className="text-gray-900">
                  {[
                    companyData.company.registered_office_address.address_line_1,
                    companyData.company.registered_office_address.address_line_2,
                    companyData.company.registered_office_address.locality,
                    companyData.company.registered_office_address.region,
                    companyData.company.registered_office_address.postal_code,
                    companyData.company.registered_office_address.country
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            )}
          </div>

          {/* Confirmation Statement */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#1a1f4e]" />
              Confirmation Statement
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Last Made Up To</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(companyData.confirmation_statement.last_made_up_to)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-500 mb-1">Next Made Up To</h3>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(companyData.confirmation_statement.next_made_up_to)}
                </p>
              </div>
              <div className={`p-4 rounded-lg ${companyData.confirmation_statement.overdue ? 'bg-red-50' : 'bg-gray-50'}`}>
                <h3 className="text-sm font-medium text-gray-500 mb-1">Next Due</h3>
                <p className={`text-lg font-semibold ${getDaysUntilDueColor(companyData.confirmation_statement.days_until_due)}`}>
                  {formatDate(companyData.confirmation_statement.next_due)}
                  {companyData.confirmation_statement.days_until_due !== null && (
                    <span className="text-sm font-normal ml-2">
                      ({companyData.confirmation_statement.days_until_due} days)
                    </span>
                  )}
                </p>
                {companyData.confirmation_statement.overdue && (
                  <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    OVERDUE
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Officers */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[#1a1f4e]" />
              Officers ({companyData.officers.active_count} active)
            </h2>

            <div className="space-y-3">
              {companyData.officers.items.map((officer, index) => (
                <div key={index} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-medium text-gray-900">{officer.name}</h3>
                      <p className="text-sm text-gray-500 capitalize">{officer.officer_role?.replace(/_/g, ' ')}</p>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-gray-500">Appointed: {formatDate(officer.appointed_on)}</p>
                      {officer.nationality && (
                        <p className="text-gray-400">Nationality: {officer.nationality}</p>
                      )}
                    </div>
                  </div>
                  {officer.occupation && (
                    <p className="text-sm text-gray-500 mt-2">Occupation: {officer.occupation}</p>
                  )}
                </div>
              ))}
              {companyData.officers.items.length === 0 && (
                <p className="text-gray-500 text-center py-4">No active officers found</p>
              )}
            </div>
          </div>

          {/* PSCs */}
          {companyData.pscs.items.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-[#1a1f4e]" />
                Persons with Significant Control ({companyData.pscs.total_count})
              </h2>

              <div className="space-y-3">
                {companyData.pscs.items.map((psc, index) => (
                  <div key={index} className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-medium text-gray-900">{psc.name}</h3>
                    {psc.natures_of_control && (
                      <p className="text-sm text-gray-500 mt-1">
                        {psc.natures_of_control.map(n => n.replace(/-/g, ' ')).join(', ')}
                      </p>
                    )}
                    <p className="text-sm text-gray-400 mt-1">Notified: {formatDate(psc.notified_on)}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Filings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#1a1f4e]" />
              Recent Filings
            </h2>

            <div className="space-y-2">
              {companyData.recent_filings.map((filing, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{filing.description || filing.type}</p>
                    <p className="text-sm text-gray-500">{filing.type}</p>
                  </div>
                  <p className="text-sm text-gray-500">{formatDate(filing.date)}</p>
                </div>
              ))}
              {companyData.recent_filings.length === 0 && (
                <p className="text-gray-500 text-center py-4">No recent filings found</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!companyData && !loading && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Search for a Company</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            Enter a company number above to view company details, officers, PSCs, and confirmation statement dates.
          </p>
        </div>
      )}
    </div>
  );
};

export default CompaniesHouse;
